import { formatDate } from '../utils/dateConverter';
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../services/api";
import "../styles/ProjectPage.css";
import ProjectModal from "../modals/ProjectModal";
import ProjectDetails from "../components/ProjectDetails";
import AssignUsersModal from "../modals/AssignUsersModal";
import {
    getProjectEntries,
    getRecentProjects,
    archiveProject,
    unarchiveProject,
} from "../services/ProjectServices";
import { useToast, useConfirm } from "../context/UIContext";
import { extractApiError } from '../utils/apiError';

function fmtDate(d) {
    if (!d) return null;
    return formatDate(d);
}

function ProjectOverview({ onSelectProject }) {
    const [recent, setRecent] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getRecentProjects()
            .then(setRecent)
            .catch(() => setRecent([]))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="proj-overview-empty">Lade Projekte…</div>;

    return (
        <div className="proj-overview">
            <div className="proj-overview-header">
                <h2 className="proj-overview-title">Projekte</h2>
                <p className="proj-overview-sub">Zuletzt aktiv · letzte 30 Tage</p>
            </div>

            {recent.length === 0 ? (
                <div className="proj-overview-empty">
                    <svg viewBox="0 0 48 48" fill="none" className="proj-overview-icon">
                        <rect x="6" y="4" width="36" height="40" rx="3" stroke="currentColor" strokeWidth="2"/>
                        <path d="M14 16h20M14 22h14M14 28h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <p>Noch keine Aktivität in den letzten 30 Tagen.</p>
                    <p className="proj-overview-empty-sub">Wähle links ein Projekt aus oder erstelle ein neues.</p>
                </div>
            ) : (
                <div className="proj-overview-grid">
                    {recent.map(p => (
                        <button
                            key={p.id}
                            className="proj-overview-card"
                            onClick={() => onSelectProject(p)}
                        >
                            <div className="proj-card-top">
                                <span className="proj-card-company">{p.company_name || '—'}</span>
                                <span className="proj-card-hours">
                                    {p.recent_hours > 0 ? `${p.recent_hours} h` : '—'}
                                </span>
                            </div>
                            <div className="proj-card-name">{p.name}</div>
                            {p.description && (
                                <div className="proj-card-desc">{p.description}</div>
                            )}
                            <div className="proj-card-footer">
                                {p.last_activity
                                    ? <>Letzte Aktivität: {fmtDate(p.last_activity)}</>
                                    : 'Keine Einträge'}
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function Project() {
    const toast = useToast();
    const confirm = useConfirm();
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editProject, setEditProject] = useState(null);
    const [showAssignUsersModal, setShowAssignUsersModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [showArchived, setShowArchived] = useState(false);
    const [loadError, setLoadError] = useState('');

    const loadProjects = useCallback(async () => {
        try {
            setProjects(await getProjectEntries(showArchived));
            setLoadError('');
        } catch {
            setLoadError('Daten konnten nicht geladen werden. Bitte versuche es erneut.');
        }
    }, [showArchived]);

    useEffect(() => { loadProjects(); }, [loadProjects]);

    const handleProjectClick = (project) => {
        const normalised = project.companyName !== undefined ? project : {
            id: project.id,
            name: project.name,
            description: project.description,
            companyId: project.company_id,
            companyName: project.company_name,
            isArchived: project.is_archived ?? false,
        };
        setSelectedProject(normalised);
    };

    const handleAddProject = () => { setEditProject(null); setShowModal(true); };
    const handleEditProject = () => { if (!selectedProject) return; setEditProject(selectedProject); setShowModal(true); };
    const handleModalSave = () => { setShowModal(false); loadProjects(); };

    const handleDeleteProject = async () => {
        if (!selectedProject) return;
        if (await confirm("Projekt wirklich löschen?", { confirmLabel: 'Löschen', danger: true })) {
            api.delete(`/Project/${selectedProject.id}`).then(() => {
                loadProjects();
                setSelectedProject(null);
            });
        }
    };

    const handleArchiveProject = async () => {
        if (!selectedProject) return;
        try {
            await archiveProject(selectedProject.id);
            setSelectedProject(null);
            loadProjects();
        } catch (err) { toast(extractApiError(err, "Archivierung fehlgeschlagen.")); }
    };

    const handleUnarchiveProject = async () => {
        if (!selectedProject) return;
        try {
            await unarchiveProject(selectedProject.id);
            setSelectedProject(prev => ({ ...prev, isArchived: false }));
            loadProjects();
        } catch (err) { toast(extractApiError(err, "Reaktivierung fehlgeschlagen.")); }
    };

    const filteredProjects = projects.filter(
        (p) =>
            (p.name && p.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (p.companyName && p.companyName.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="project-page">

            {loadError && (
                <div className="alert alert--error" style={{ margin: '0 0 16px' }}>
                    {loadError}
                    <button className="alert__close" onClick={loadProjects} style={{ marginLeft: 12 }}>Erneut versuchen</button>
                </div>
            )}

            <div className="project-list-panel">
                <div className="proj-list-header">
                    <h3>Projekte</h3>
                    <button className="proj-add-btn" onClick={handleAddProject}>+</button>
                </div>

                <input
                    type="text"
                    placeholder="Projekt suchen…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="project-search"
                />

                <div className="proj-list-items">
                    {filteredProjects.map((p) => (
                        <div
                            key={p.id}
                            className={`project-item${selectedProject?.id === p.id ? " active" : ""}${p.isArchived ? " archived" : ""}`}
                            onClick={() => handleProjectClick(p)}
                        >
                            <span className="proj-item-company">{p.companyName}</span>
                            <span className="proj-item-name">{p.name}</span>
                            {p.isArchived && <span className="proj-item-badge">Archiviert</span>}
                        </div>
                    ))}
                </div>

                <button
                    className={`proj-archive-toggle${showArchived ? " active" : ""}`}
                    onClick={() => { setShowArchived(v => !v); setSelectedProject(null); }}
                >
                    {showArchived ? "Archiv ausblenden" : "Archivierte anzeigen"}
                </button>
            </div>

            <div className="project-details-panel">
                {selectedProject ? (
                    <>
                        <div className="proj-detail-header">
                            <div>
                                <h2 className="proj-detail-title">{selectedProject.name}</h2>
                                {selectedProject.companyName && (
                                    <p className="proj-detail-company">{selectedProject.companyName}</p>
                                )}
                                {selectedProject.description && (
                                    <p className="proj-detail-desc">{selectedProject.description}</p>
                                )}
                            </div>
                            <div className="proj-detail-actions">
                                <Link
                                    className="proj-action-btn proj-action-link"
                                    to={`/project-tickets?project=${encodeURIComponent(selectedProject.id)}`}
                                    title={`Tickets von ${selectedProject.name} öffnen`}
                                >
                                    Tickets
                                </Link>
                                <button className="proj-action-btn" onClick={handleEditProject}>Bearbeiten</button>
                                <button className="proj-action-btn" onClick={() => setShowAssignUsersModal(true)}>
                                    User zuweisen
                                </button>
                                {selectedProject.isArchived ? (
                                    <button className="proj-action-btn" onClick={handleUnarchiveProject}>
                                        Reaktivieren
                                    </button>
                                ) : (
                                    <button className="proj-action-btn" onClick={handleArchiveProject}>
                                        Archivieren
                                    </button>
                                )}
                                <button className="proj-action-btn danger" onClick={handleDeleteProject}>
                                    Löschen
                                </button>
                            </div>
                        </div>

                        <ProjectDetails project={selectedProject} />
                    </>
                ) : (
                    <ProjectOverview onSelectProject={handleProjectClick} />
                )}
            </div>

            {showModal && (
                <ProjectModal project={editProject} onClose={() => setShowModal(false)} onSave={handleModalSave} />
            )}
            {showAssignUsersModal && selectedProject && (
                <AssignUsersModal projectId={selectedProject.id} onClose={() => setShowAssignUsersModal(false)} />
            )}
        </div>
    );
}